'use client';

import { Typography } from 'src/components/PulseUI/Typography';
import { useGetCrmObjectsQuery } from 'src/api/redux';
import { useFormContext } from 'react-hook-form';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';

interface ControlledRowProps {
  name: string;
  label: string;
  options?: any[];
  crmType?: string;
  integration?: string;
  writableOnly?: boolean;
}

const ControlledRow = ({ ...props }: ControlledRowProps) => {
  const { watch } = useFormContext();
  const isExtraOptions = props?.name === 'extra.options';
  const isThirdParty = props.integration === 'third_party';

  const optionNameMap = {
    auto_populate: 'Autopopulate',
    user_input_lookup: 'Lookup',
    user_input_custom: 'User Input',
  };

  const { data: crmObjects } = useGetCrmObjectsQuery(
    {
      crmType: props.crmType,
      crm: props.integration,
      writable_only: props.writableOnly,
    },
    {
      skip: isThirdParty || !props.crmType || !props.integration,
    }
  );

  const value = watch(props.name);

  if (!value) return null;

  const rowOptionsMap = {
    crm_api_code: crmObjects,
    'extra.search_label': crmObjects,
    'extra.value_field': crmObjects,
    attribute_code: props.options,
    'extra.crm_object_type': props.options,
    input_type: props.options,
  };

  const isInOptions = rowOptionsMap[props.name]?.find(
    (option) => option.value === value || option.code === value
  );

  const isDateRange = props.name === 'secondary_inputs.year_range';
  const isSavedSearch = props.name === 'secondary_inputs.saved_search';

  const displayValue =
    isDateRange && value?.gte && value?.lte
      ? `${value?.gte} - ${value?.lte}`
      : isSavedSearch
      ? props.options?.find((option) => option.value === value)?.label
      : isInOptions?.label || optionNameMap[value] || sentenceCase(value);

  return (
    <div className="mt-2 flex items-center justify-between border-b border-neutral-300 w-full">
      <Typography variant="h5" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
        {props.label}
      </Typography>
      {isExtraOptions ? (
        <div className="flex items-center justify-end gap-1">
          {value?.map((option, index) => {
            const isLast = index === value.length - 1;
            return (
              <span key={index}>
                {[option?.value, option?.label]?.filter(Boolean).join(': ')}
                {!isLast && ','}
              </span>
            );
          })}
        </div>
      ) : (
        <Typography>{displayValue}</Typography>
      )}
    </div>
  );
};

export default ControlledRow;
